import { type InspirationEditorState } from '$/pages/InspirationEditorPage/stores/useInspirationEditorStore';

type SelectorState = Pick<
  InspirationEditorState,
  'inspiration' | 'currentViewId' | 'componentOverwrites' | 'editMasterData'
>;

export const isComponentVisible = (
  state: Pick<InspirationEditorState, 'inspiration' | 'componentOverwrites'>,
  componentId: string,
): boolean =>
  state.componentOverwrites[componentId]?.isVisible ??
  state.inspiration?.components[componentId]?.isVisible ??
  true;

export const getCurrentView = (
  state: SelectorState,
  viewId = state.currentViewId,
) => state.inspiration?.views.find((view) => view?.id === viewId);

export const selectViewComponents = (state: SelectorState) => {
  const view = getCurrentView(state);
  if (!view || !state.inspiration) return [];

  const viewComponentIds = new Set(
    (view.components ?? []).filter(Boolean).map(({ id }) => id),
  );

  return Object.values(state.inspiration.components).filter((component) =>
    viewComponentIds.has(component.id),
  );
};

export const selectViewComponentIds = (
  state: SelectorState,
  viewId?: number,
) => {
  const view = getCurrentView(state, viewId);
  return view?.components?.filter(Boolean).map((c) => c.id) ?? [];
};

export const getVisibleComponentIds = (
  state: SelectorState,
  viewId?: number,
) => {
  const componentIds = selectViewComponentIds(state, viewId);

  if (state.editMasterData) return componentIds;

  return componentIds.filter((id) => isComponentVisible(state, id));
};

export const getBorderAssets = (state: SelectorState, viewId?: number) => {
  const view = getCurrentView(state, viewId);
  if (!view || !state.inspiration) return [];

  return selectViewComponentIds(state, viewId)
    .filter((id) => state.inspiration!.render.borders[id])
    .map((id) => ({
      componentId: id,
      url: state.inspiration!.render.borders[id].replace(
        '{view}',
        String(view.id),
      ),
    }));
};

export const getBorderElementIds = (state: SelectorState, viewId?: number) =>
  selectViewComponentIds(state, viewId).map((id) => `border/${id}`);

export const getCornerElementIds = (state: SelectorState, viewId?: number) =>
  selectViewComponentIds(state, viewId).map((id) => `corners/${id}`);
